import { useId } from 'react'

import { Button, CodeEntry, DiagnosticCopy } from '@krp/ui-kit'

import './own-cashier.css'

export interface SaleCashierPanelProps {
  list: { status: 'loading' } | { status: 'error' } | { status: 'ready'; items: readonly { id: string; name: string }[] }
  selectedId: string | null
  /** Имя подтверждённого кассира (из ответа службы Cashiers); `null` — подтверждения нет. */
  grantedName: string | null
  verifying: boolean
  /** Ошибка службы Cashiers: текст сервера дословно, код и номер запроса — для передачи администратору. */
  problem: { text: string; code: string | null; correlationId: string | null } | null
  /** Почему деньги закрыты; `null` — кассир подтверждён. */
  blockReason: string | null
  /** Текст для «Скопировать»: ошибка целиком, иначе причина закрытия денег. */
  copyText: string | null
  /** Кассир незавершённой операции, если не тот, кто выбран сейчас: повтор уйдёт на него. */
  retryCashierName: string | null
  onSelect: (id: string | null) => void
  /** Код уходит наружу один раз и в поле не остаётся. */
  onSubmitPin: (pin: string) => void
  onReload: () => void
  onForget: () => void
}

/**
 * Выбор кассира и его код над экраном продажи «Собственной» (RP-OWN11). Показывает то, что даёт окно: причина закрытия денег и ошибка
 * службы Cashiers стоят на экране, пока их не заменит действие кассира; код живёт только в поле и сбрасывается при отправке.
 */
export function SaleCashierPanel(props: SaleCashierPanelProps) {
  const selectId = useId()
  const { list, problem, grantedName, blockReason, verifying, selectedId, copyText } = props

  return (
    <section className="own-cashier" aria-label="Кассир продажи">
      {grantedName !== null ? (
        <p className="own-cashier__who">
          <span aria-hidden="true">✔ </span>
          <strong>Кассир: {grantedName}</strong>
          <Button variant="ghost" onClick={props.onForget}>
            Сменить кассира
          </Button>
        </p>
      ) : (
        <div className="own-cashier__form">
          <label htmlFor={selectId}>Кассир</label>
          <select
            id={selectId}
            value={selectedId ?? ''}
            disabled={list.status !== 'ready' || list.items.length === 0 || verifying}
            onChange={(event) => props.onSelect(event.target.value === '' ? null : event.target.value)}
          >
            <option value="">— выберите —</option>
            {list.status === 'ready' && list.items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
          {list.status === 'error' && (
            <Button variant="ghost" onClick={props.onReload}>
              Прочитать заново
            </Button>
          )}
          <CodeEntry label="Код кассира" submitLabel="Войти" busy={verifying} disabled={selectedId === null} onSubmit={props.onSubmitPin} />
        </div>
      )}
      {verifying && <p role="status">Проверяем код…</p>}
      {props.retryCashierName !== null && (
        <p role="status">Незавершённая операция начата на кассира «{props.retryCashierName}»: повтор уйдёт на него, пока вы не закончите операцию.</p>
      )}
      {problem !== null && (
        <div role="alert" className="own-cashier__problem">
          <p>
            <span aria-hidden="true">✖ </span>
            {problem.text}
          </p>
          {problem.code !== null && <p>Код: {problem.code}</p>}
          {problem.correlationId !== null && <p>Номер запроса: {problem.correlationId}</p>}
        </div>
      )}
      {blockReason !== null && <p role="status" className="own-cashier__reason">{blockReason}</p>}
      {copyText !== null && <DiagnosticCopy text={copyText} label="Скопировать" />}
    </section>
  )
}
